import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import {
  Shield, Trophy, Edit2, Save, Crown, MapPin, Zap, Camera, Award, Share2, Calendar
} from "lucide-react";
import { format } from "date-fns";

const badges = [
  { id: "first_krew", icon: "🎉", label: "First Krew", check: (p: any) => (p?.total_krews_joined || 0) >= 1 },
  { id: "five_nights", icon: "🌙", label: "5 Nights Out", check: (p: any) => (p?.total_krews_attended || 0) >= 5 },
  { id: "elite", icon: "⭐", label: "Elite Reliability", check: (p: any) => (p?.reliability_score || 0) >= 95 },
  { id: "reviewer", icon: "📝", label: "Venue Reviewer", check: (_: any, reviewCount: number) => reviewCount >= 1 },
];

const Profile = () => {
  const { user, profile, refreshProfile } = useAuth();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [editing, setEditing] = useState(false);
  const [displayName, setDisplayName] = useState(profile?.display_name || "");
  const [bio, setBio] = useState(profile?.bio || "");
  const [city, setCity] = useState(profile?.city || "Bangalore");
  const [phone, setPhone] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [pastKrews, setPastKrews] = useState<any[]>([]);
  const [reviewCount, setReviewCount] = useState(0);

  useEffect(() => {
    if (!user) return;
    const fetchHistory = async () => {
      const [krewsRes, reviewsRes] = await Promise.all([
        supabase.from("krew_members").select("krew_id, krews(title, event_date, status)").eq("user_id", user.id).order("joined_at", { ascending: false }),
        supabase.from("venue_reviews").select("id", { count: "exact", head: true }).eq("user_id", user.id),
      ]);
      if (krewsRes.data) setPastKrews(krewsRes.data.filter(k => k.krews));
      setReviewCount(reviewsRes.count || 0);
    };
    fetchHistory();
  }, [user]);

  useEffect(() => {
    if (profile) {
      setDisplayName(profile.display_name || "");
      setBio(profile.bio || "");
      setCity(profile.city || "Bangalore");
    }
  }, [profile]);

  // Load phone from the private table (separate from public profile fields)
  useEffect(() => {
    if (!user) return;
    (async () => {
      const { data } = await supabase
        .from("profile_private")
        .select("phone")
        .eq("user_id", user.id)
        .maybeSingle();
      if (data?.phone) setPhone(data.phone);
    })();
  }, [user]);

  const handleSave = async () => {
    if (!user) return;
    setSaving(true);
    try {
      const { error } = await supabase
        .from("profiles")
        .update({ display_name: displayName, bio, city })
        .eq("user_id", user.id);
      if (error) throw error;
      const { error: phoneError } = await supabase
        .from("profile_private")
        .upsert({ user_id: user.id, phone: phone || null }, { onConflict: "user_id" });
      if (phoneError) throw phoneError;
      await refreshProfile();
      setEditing(false);
      toast({ title: "Profile updated! ✨" });
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally { setSaving(false); }
  };

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!user || !e.target.files?.[0]) return;
    setUploading(true);
    try {
      const file = e.target.files[0];
      const ext = file.name.split(".").pop();
      const path = `${user.id}/avatar.${ext}`;
      const { error: uploadError } = await supabase.storage.from("avatars").upload(path, file, { upsert: true });
      if (uploadError) throw uploadError;
      const { data: { publicUrl } } = supabase.storage.from("avatars").getPublicUrl(path);
      await supabase.from("profiles").update({ avatar_url: publicUrl }).eq("user_id", user.id);
      await refreshProfile();
      toast({ title: "Avatar updated! 📸" });
    } catch (error: any) {
      toast({ title: "Upload failed", description: error.message, variant: "destructive" });
    } finally { setUploading(false); }
  };

  const shareProfile = () => {
    const url = `${window.location.origin}/profile`;
    navigator.clipboard.writeText(url);
    toast({ title: "Profile link copied! 🔗" });
  };

  const reliabilityColor = (score: number) => score >= 90 ? "text-green-400" : score >= 70 ? "text-secondary" : "text-destructive";
  const reliabilityLevel = (score: number) => score >= 95 ? "Elite" : score >= 85 ? "Reliable" : score >= 70 ? "Good" : "Needs Work";

  return (
    <div className="mx-auto max-w-2xl space-y-8">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-foreground">Your <span className="text-gradient-neon">Profile</span></h1>
        <div className="flex gap-2">
          <button onClick={shareProfile} className="flex items-center gap-2 rounded-xl border border-border px-4 py-2 text-sm text-muted-foreground hover:text-foreground">
            <Share2 className="h-4 w-4" />
          </button>
          {!editing ? (
            <button onClick={() => setEditing(true)} className="flex items-center gap-2 rounded-xl border border-border px-4 py-2 text-sm text-muted-foreground hover:text-foreground">
              <Edit2 className="h-4 w-4" /> Edit
            </button>
          ) : (
            <button onClick={handleSave} disabled={saving} className="flex items-center gap-2 rounded-xl bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50">
              <Save className="h-4 w-4" /> {saving ? "Saving..." : "Save"}
            </button>
          )}
        </div>
      </div>

      {/* Avatar + Name */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="flex items-center gap-5 rounded-2xl border border-border bg-card p-6">
        <div className="relative">
          {profile?.avatar_url ? (
            <img src={profile.avatar_url} alt="Avatar" className="h-20 w-20 rounded-2xl object-cover" />
          ) : (
            <div className="flex h-20 w-20 items-center justify-center rounded-2xl bg-primary/20 text-3xl font-bold text-primary">
              {profile?.display_name?.charAt(0)?.toUpperCase() || "?"}
            </div>
          )}
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="absolute -bottom-2 -right-2 flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg"
          >
            <Camera className="h-4 w-4" />
          </button>
          <input ref={fileInputRef} type="file" accept="image/*" onChange={handleAvatarUpload} className="hidden" />
        </div>
        <div className="flex-1">
          {editing ? (
            <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} className="mb-1 w-full rounded-lg border border-border bg-muted px-3 py-2 text-lg font-bold text-foreground focus:border-primary focus:outline-none" />
          ) : (
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-bold text-foreground">{profile?.display_name}</h2>
              {profile?.is_premium && <Crown className="h-5 w-5 text-secondary" />}
            </div>
          )}
          <p className="text-sm text-muted-foreground">{user?.email}</p>
        </div>
      </motion.div>

      {/* Badges */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="rounded-2xl border border-border bg-card p-6">
        <div className="mb-4 flex items-center gap-3">
          <Award className="h-6 w-6 text-secondary" />
          <h3 className="text-lg font-bold text-foreground">Badges</h3>
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {badges.map(b => {
            const earned = b.check(profile, reviewCount);
            return (
              <div key={b.id} className={`rounded-xl border p-4 text-center transition-all ${earned ? "border-secondary/30 bg-secondary/5" : "border-border bg-muted/50 opacity-40"}`}>
                <span className="text-2xl">{b.icon}</span>
                <p className="mt-1 text-xs font-medium text-foreground">{b.label}</p>
              </div>
            );
          })}
        </div>
      </motion.div>

      {/* Reliability Score */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="rounded-2xl border border-border bg-card p-6">
        <div className="mb-4 flex items-center gap-3"><Shield className="h-6 w-6 text-primary" /><h3 className="text-lg font-bold text-foreground">Reliability Score</h3></div>
        <div className="flex items-end gap-4">
          <span className={`text-5xl font-bold ${reliabilityColor(profile?.reliability_score || 100)}`}>{profile?.reliability_score || 100}%</span>
          <span className="mb-1 text-lg font-medium text-muted-foreground">{reliabilityLevel(profile?.reliability_score || 100)}</span>
        </div>
        <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-muted"><div className="h-full rounded-full bg-primary transition-all" style={{ width: `${profile?.reliability_score || 100}%` }} /></div>
        <p className="mt-3 text-xs text-muted-foreground">Show up when you commit. High scores unlock perks.</p>
      </motion.div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4">
        {[
          { icon: Zap, value: profile?.total_krews_joined || 0, label: "Krews Joined", color: "text-primary" },
          { icon: Trophy, value: profile?.total_krews_attended || 0, label: "Nights Attended", color: "text-secondary" },
        ].map((s, i) => (
          <motion.div key={s.label} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 + i * 0.1 }} className="rounded-2xl border border-border bg-card p-5">
            <s.icon className={`mb-2 h-5 w-5 ${s.color}`} /><p className="text-3xl font-bold text-foreground">{s.value}</p><p className="text-sm text-muted-foreground">{s.label}</p>
          </motion.div>
        ))}
      </div>

      {/* Bio & Details */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="rounded-2xl border border-border bg-card p-6 space-y-4">
        <h3 className="text-lg font-bold text-foreground">About</h3>
        {editing ? (
          <>
            <div><label className="mb-1 block text-sm text-muted-foreground">Bio</label><textarea value={bio} onChange={(e) => setBio(e.target.value)} rows={3} placeholder="Tell your crew about you..." className="w-full rounded-lg border border-border bg-muted px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none resize-none" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className="mb-1 block text-sm text-muted-foreground">City</label><select value={city} onChange={(e) => setCity(e.target.value)} className="w-full rounded-lg border border-border bg-muted px-3 py-2 text-sm text-foreground focus:border-primary focus:outline-none"><option>Bangalore</option><option>Mumbai</option><option>Delhi</option><option>Hyderabad</option><option>Pune</option><option>Goa</option><option>Chennai</option></select></div>
              <div><label className="mb-1 block text-sm text-muted-foreground">Phone</label><input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+91..." className="w-full rounded-lg border border-border bg-muted px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none" /></div>
            </div>
          </>
        ) : (
          <>
            <p className="text-sm text-muted-foreground">{profile?.bio || "No bio yet"}</p>
            <div className="flex items-center gap-2 text-sm text-muted-foreground"><MapPin className="h-4 w-4" /> {profile?.city || "Bangalore"}</div>
          </>
        )}
      </motion.div>

      {/* Krew History */}
      {pastKrews.length > 0 && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }} className="rounded-2xl border border-border bg-card p-6">
          <div className="mb-4 flex items-center gap-3"><Calendar className="h-6 w-6 text-primary" /><h3 className="text-lg font-bold text-foreground">Krew History</h3></div>
          <div className="space-y-3">
            {pastKrews.slice(0, 10).map((k: any) => (
              <div key={k.krew_id} className="flex items-center justify-between rounded-lg border border-border bg-muted/30 px-4 py-3">
                <div>
                  <p className="text-sm font-medium text-foreground">{(k.krews as any)?.title}</p>
                  <p className="text-xs text-muted-foreground">{(k.krews as any)?.event_date ? format(new Date((k.krews as any).event_date), "MMM d, yyyy") : ""}</p>
                </div>
                <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${
                  (k.krews as any)?.status === "completed" ? "bg-green-500/20 text-green-400" : "bg-muted text-muted-foreground"
                }`}>{(k.krews as any)?.status}</span>
              </div>
            ))}
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default Profile;
